<script>
  import { format } from 'svelte-i18n';
  import { Card, CardBody } from '@sveltestrap/sveltestrap';

  import { CopyText } from '@client/components/copy-text';
  import {
    KeyValueField,
    RoleBindingsField,
    SwitchField,
    TextareaField,
    TextField
  } from '@client/components/fields/standalone';
  import { ScrollableSection } from '@client/components/scrollspy';
  import { ShowMore } from '@client/components/show-more';

  import { disabledValue, enabledValue } from '@client/pages/manage/domain-settings';

  import { userStore } from '@client/stores/user';

  import { formatPEMCertificate } from '@common/utils/strings';
  import { isNumerical } from '@common/utils/validation';

  export let settings = {};
  export let onUpdate = null;

  let passwordComplexity = [
    {
      name: 'uppercase',
      value: settings.authPolicy.uppercase ?? 1
    },
    {
      name: 'lowercase',
      value: settings.authPolicy.lowercase ?? 1
    },
    {
      name: 'digits',
      value: settings.authPolicy.digits ?? 1
    },
    {
      name: 'minlength',
      value: settings.authPolicy.minlength ?? 7
    },
    {
      name: 'specialchars',
      value: settings.authPolicy.specialchars ?? 1
    }
  ];

  const onPasswordComplexity = (...args) => {
    passwordComplexity.forEach(({ name, value }) => {
      settings.authPolicy[name] = Number(value);
    });

    onUpdate(...args);
  };
</script>

<div id="sso">
  <ScrollableSection id="sso-authentication">
    <h1 class="mb-3">{$format('label.AUTHENTICATION')}</h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="authPolicy-disablePassword"
          label={$format('label.LOCAL_PASSWORD')}
          tooltip={$format('label.TOOLTIP.LOCAL_PASSWORD')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          inverted
          bind:checked={settings.authPolicy.disablePassword}
          {onUpdate}
        />

        {#if !settings.authPolicy.disablePassword}
          <SwitchField
            id="authPolicy-enable2FA"
            label={$format('label.LOCAL_PASSWORD_2FA')}
            tooltip={$format('label.TOOLTIP.LOCAL_PASSWORD_2FA')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.authPolicy.enable2FA}
            {onUpdate}
          />

          <KeyValueField
            id="authPolicy"
            label={$format('label.LOCAL_PASSWORD_COMPLEXITY')}
            tooltip={$format('label.TOOLTIP.LOCAL_PASSWORD_COMPLEXITY')}
            keyColumnLabel={$format('label.RULE')}
            keyReadOnly
            valueColumnLabel={$format('label.VALUE')}
            valueValidation={isNumerical}
            valueValidationError={$format('label.VALIDATION_ERROR_ALLOWED_NUMBERS')}
            hideActionButtons
            bind:values={passwordComplexity}
            onUpdate={onPasswordComplexity}
          />
        {/if}

        {#if settings.office365enabled || $userStore.isStaff}
          <SwitchField
            id="authPolicy-disableOffice365"
            label={$format('label.MICROSOFT_365_SSO')}
            tooltip={$format('label.TOOLTIP.MICROSOFT_365_SSO')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            inverted
            bind:checked={settings.authPolicy.disableOffice365}
            {onUpdate}
          />
        {/if}

        {#if settings.googleenabled || $userStore.isStaff}
          <SwitchField
            id="authPolicy-disableGoogle"
            label={$format('label.GOOGLE_SSO')}
            tooltip={$format('label.TOOLTIP.GOOGLE_SSO')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            inverted
            bind:checked={settings.authPolicy.disableGoogle}
            {onUpdate}
          />
        {/if}

        <SwitchField
          id="authPolicy-disableSalesforce"
          label={$format('label.SALESFORCE_SSO')}
          tooltip={$format('label.TOOLTIP.SALESFORCE_SSO')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          inverted
          bind:checked={settings.authPolicy.disableSalesforce}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="sso-bindings">
    <h1 class="mb-3">{$format('label.ROLE_BINDINGS')}</h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <RoleBindingsField
          id="rolebindings-domainadmin"
          label={$format('label.ADMINS')}
          tooltip={$format('label.TOOLTIP.ADMINS')}
          value={settings.rolebindings.domainadmin}
          {onUpdate}
        />

        <RoleBindingsField
          id="rolebindings-marketing"
          label={$format('label.MARKETING')}
          tooltip={$format('label.TOOLTIP.MARKETING')}
          value={settings.rolebindings.marketing}
          {onUpdate}
        />

        <RoleBindingsField
          id="rolebindings-signatures"
          label={$format('label.SIGNATURES')}
          tooltip={$format('label.TOOLTIP.SIGNATURES')}
          value={settings.rolebindings.signatures}
          {onUpdate}
        />

        <SwitchField
          id="rolebindingsswitchdomains"
          label={$format('label.TRANSFER_ROLE_BINDINGS')}
          tooltip={$format('label.TOOLTIP.TRANSFER_ROLE_BINDINGS')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.rolebindingsswitchdomains}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="sso-saml">
    <h1 class="mb-3">{$format('label.SAML')}</h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="enablesaml"
          label={$format('label.ENABLE_SAML')}
          tooltip={$format('label.TOOLTIP.ENABLE_SAML')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.enablesaml}
          {onUpdate}
        />

        {#if settings.enablesaml}
          <TextField
            id="samlProvider"
            label={$format('label.SAML_PROVIDER_NAME')}
            tooltip={$format('label.TOOLTIP.SAML_PROVIDER_NAME')}
            bind:value={settings.samlProvider}
            {onUpdate}
          >
            {#if settings.samlProvider}
              <ShowMore collapsedText="Show preview" expandedText="Hide preview">
                <div class="saml-logo-preview">
                  <div class="bold">{$format('label.PREVIEW')}</div>
                  <div class="sso-button">
                    <span>{settings.samlProvider}</span>
                  </div>
                </div>
              </ShowMore>
            {/if}
          </TextField>

          <TextField
            id="samlLogo"
            label={$format('label.SAML_PROVIDER_LOGO')}
            tooltip={$format('label.TOOLTIP.SAML_PROVIDER_LOGO')}
            bind:value={settings.samlLogo}
            {onUpdate}
          >
            {#if settings.samlLogo}
              <ShowMore collapsedText="Show preview" expandedText="Hide preview">
                <div class="saml-logo-preview">
                  <div class="bold">{$format('label.PREVIEW')}</div>
                  <div class="sso-button">
                    <img src={settings.samlLogo} alt="SAML Logo" />
                    <span>{settings.samlProvider || $format('label.PROVIDER_NAME')}</span>
                  </div>
                </div>
              </ShowMore>
            {/if}
          </TextField>

          <TextField
            id="samlEntryPoint"
            label={$format('label.SAML_IDP_URL')}
            tooltip={$format('label.TOOLTIP.SAML_IDP_URL')}
            bind:value={settings.samlEntryPoint}
            {onUpdate}
            allowCopy
          >
            <div class="pt-2">
              <CopyText
                label={$format('label.SAML_IDP_URL_COPY_TEXT')}
                source="https://accounts.opensense.com/auth/saml/callback"
              />
            </div>
          </TextField>

          <TextField
            id="samlIssuer"
            label={$format('label.SAML_IDP_ISSUER')}
            tooltip={$format('label.TOOLTIP.SAML_IDP_ISSUER')}
            bind:value={settings.samlIssuer}
            {onUpdate}
            allowCopy
          >
            <div class="pt-2">
              <CopyText label={$format('label.SAML_IDP_ISSUER_COPY_TEXT')} source="http://samlsp.opensense.com" />
            </div>
          </TextField>

          <TextareaField
            id="samlCert"
            label={$format('label.SAML_IDP_CERTIFICATE')}
            tooltip={$format('label.TOOLTIP.SAML_IDP_CERTIFICATE')}
            bind:value={settings.samlCert}
            formatInput={formatPEMCertificate}
            formatValue={(value) => `<pre>${value}</pre>`}
            {onUpdate}
            allowCopy
          />
        {/if}
      </CardBody>
    </Card>
  </ScrollableSection>
</div>

<style lang="scss" src="./sso.scss"></style>
